<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Course' : 'Add Course'"
      :container-class="null"
      :show-back="true"
      class="mb-32pt"
    />

    <page-separator title="Course" />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Image" label-for="image" label-class="form-label" class="row-align-items-center">
        <b-media class="align-items-center" vertical-align="center">
          <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
            <span v-if="isImgUploading">...</span>
            <b-img :src="course.image" class="img-fluid" width="40" alt="Image" v-else-if="course.image" />
            <i class="fas fa-university fa-lg" v-else></i>
          </fmv-avatar>

          <image-uploader
            @image="e => setImage(e, 'image')"
            :isUploading="isImgUploading"
            :isRequired="!course.image"
            placeholder="Select Image"
          />
        </b-media>
      </b-form-group>

      <b-form-group label="Title" label-for="title" label-class="form-label">
        <b-form-input id="title" placeholder="Enter Course's Title" v-model="course.title" required />
      </b-form-group>

      <b-form-group label="Custom Tags" label-for="custom_tags" label-class="form-label">
        <b-form-tags
          id="custom_tags"
          v-model="course.customTags"
          separator=","
          placeholder="Type a tag and press enter"
          remove-on-delete
          tag-variant="info"
          class="form-control h-auto"
        />
        <small class="form-text text-muted">Press enter or comma to add a tag.</small>
      </b-form-group>

      <b-form-group label="Short Introduction" label-for="short_introduction" label-class="form-label">
        <b-form-textarea
          id="short_introduction"
          placeholder="Enter Course's Short Introduction"
          v-model="course.shortIntro"
          required
          :rows="3"
        />
      </b-form-group>

      <b-form-group label="Description" label-for="description" label-class="form-label">
        <editor
          id="description"
          name="description"
          :init="{
            height: 400,
            menubar: 'edit view insert format tools table help',
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount',
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
             alignleft aligncenter alignright alignjustify | \
             bullist numlist outdent indent | removeformat | help',
          }"
          v-model="course.description"
          placeholder="Enter Course Description."
        />
        <b-form-invalid-feedback :state="!$v.course.description.required && $v.course.description.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group label="Program Category" label-for="prog_category" label-class="form-label">
        <v-select
          required
          id="prog_category"
          class="form-control v-select-custom"
          label="title"
          v-model="course.progCategory"
          :reduce="item => item.id"
          placeholder="Select Program Category"
          :options="allPrograms"
          :disabled="!canUpdateCategory"
        >
        </v-select>
        <b-form-invalid-feedback
          :state="!$v.course.progCategory.required && $v.course.progCategory.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group label="Assigned Schools" label-for="assigned_schools" label-class="form-label">
        <v-select
          id="assigned_schools"
          class="form-control v-select-custom"
          label="name"
          v-model="course.assignedSchools"
          :reduce="item => item.id"
          placeholder="Select Schools (Multiple)"
          :options="allSchools"
          :loading="areSchoolsLoading"
          multiple
        >
        </v-select>
      </b-form-group>
      <b-form-group label="Course Fee" label-for="course_fee" label-class="form-label">
        <b-input-group prepend="$">
          <b-form-input
            id="course_fee"
            placeholder="Enter Course Fee"
            v-model.number="course.courseFee"
            type="number"
            min="0"
            step="1"
          />
        </b-input-group>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox id="checkbox-4" v-model="course.isPublished" name="checkbox-4" switch
          >Course Published?
        </b-form-checkbox>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox
          id="checkbox-5"
          v-model="course.restrictNextLesson"
          name="checkbox-5"
          switch
          :disabled="course.requirePassToAdvance"
          >Restrict Next Lesson?
          <i
            v-if="course.requirePassToAdvance"
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="
              `Required by 'Require Passing Grade to Advance': students can only be held back for failing an assessment if content is unlocked in order. Turn that setting off to change this.`
            "
            >lock</i
          >
        </b-form-checkbox>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox id="checkbox-6" v-model="course.restrictionOnStartDate" name="checkbox-6" switch
          >Restriction on Start Date?
        </b-form-checkbox>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox id="checkbox-7" v-model="course.enableSkillLibrary" name="checkbox-7" switch
          >Enable Skill Library?
        </b-form-checkbox>
      </b-form-group>

      <page-separator title="State Compliance" />
      <p class="text-muted small mb-3">
        Only enable these for courses that must meet state training requirements (e.g. CA CNA).
        Existing courses are unaffected while these stay off.
      </p>
      <b-form-group>
        <b-form-checkbox id="checkbox-enforce-read-time" v-model="course.enforceReadTime" name="checkbox-enforce-read-time" switch
          >Enforce Read Time?
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="
              `Students must spend each lesson's Estimated Content Time actively reading before they can mark it complete. Time is measured on the server; idle time is not counted.`
            "
            >info</i
          >
        </b-form-checkbox>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox id="checkbox-pass-to-advance" v-model="course.requirePassToAdvance" name="checkbox-pass-to-advance" switch
          >Require Passing Grade to Advance?
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="
              `Students must PASS each quiz or exam before moving on, not merely attempt it. If a student runs out of attempts, an admin can grant more.`
            "
            >info</i
          >
        </b-form-checkbox>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox id="checkbox-identity-attestation" v-model="course.requireIdentityAttestation" name="checkbox-identity-attestation" switch
          >Require Identity PIN &amp; Attestation?
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="
              `Students set a PIN and sign a penalty-of-perjury statement, then re-enter the PIN before each exam.`
            "
            >info</i
          >
        </b-form-checkbox>
      </b-form-group>
      <div class="row">
        <div class="col-md-6">
          <b-form-group label-for="minutes_per_credit_hour">
            <label for="minutes_per_credit_hour" class="form-label"
              >Minutes per Credit Hour
              <i
                class="material-icons icon-16pt clickable-item text-muted"
                v-b-popover.hover.top="
                  `How many minutes of instruction count as one credit hour. California uses 50.`
                "
                >info</i
              ></label
            >
            <b-form-input
              id="minutes_per_credit_hour"
              placeholder="50"
              v-model.number="course.minutesPerCreditHour"
              type="number"
              min="1"
            />
          </b-form-group>
        </div>
        <div class="col-md-6">
          <b-form-group label-for="required_theory_hours">
            <label for="required_theory_hours" class="form-label"
              >Required Theory Hours
              <i
                class="material-icons icon-16pt clickable-item text-muted"
                v-b-popover.hover.top="
                  `Total theory hours this course must deliver. Shown on transcripts and reports; it does not block students. California CNA requires 60.`
                "
                >info</i
              ></label
            >
            <b-form-input
              id="required_theory_hours"
              placeholder="e.g. 60"
              v-model.number="course.requiredTheoryHours"
              type="number"
              min="0"
            />
          </b-form-group>
        </div>
      </div>
      <b-form-group label-for="course_policy">
        <label for="course_policy" class="form-label"
          >Course Policy
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="
              `Shown to students as a Course Policy tab in the LMS. Use for attendance and absence policy, makeup work, grade requirements, deadlines, equipment failure and privacy. Leave blank to hide the tab.`
            "
            >info</i
          ></label
        >
        <editor
          :key="'policy-editor-' + editorKey"
          :api-key="tinemceApiKey"
          :init="{
            height: 400,
            menubar: false,
            plugins: [
              'advlist autolink lists link',
              'searchreplace visualblocks',
              'insertdatetime table paste',
              'help wordcount code preview',
            ],
            toolbar:
              'undo redo | formatselect | bold italic | bullist numlist outdent indent | link | removeformat | code preview | help',
            statusbar: false,
            branding: false,
          }"
          v-model="course.coursePolicy"
        />
      </b-form-group>
      <div class="row mb-4">
        <span class="form-label col-md-6">Intro Video</span>
        <div class="col-md-6" style="display: flex; justify-content: end;">
          <b-btn
            v-if="!course.previewVideoUrl"
            variant="primary"
            :class="{ 'mb-2': course.previewVideoUrl }"
            style="width: 150px"
            @click.prevent="$refs.uploader.click()"
            class="btn-normal"
            :disabled="isUploadingVideoUrl"
          >
            <i v-if="isUploadingVideoUrl" class="fas fa-circle-notch fa-spin"></i>
            <span v-else>Upload Intro</span>
          </b-btn>
          <b-btn
            v-else
            variant="danger"
            :class="{ 'mb-2': course.previewVideoUrl }"
            style="width: 150px"
            @click.prevent="removeVideo"
            class="btn-normal"
            :disabled="isUploadingVideoUrl"
          >
            <span>Remove Video</span>
          </b-btn>
          <input type="file" ref="uploader" class="d-none" @change="handleFileChange" accept="video/*" />
        </div>
        <div v-if="course.previewVideoUrl" class="d-flex align-items-center col">
          <video
            style="width: 100%; margin: auto;"
            :style="{ height: isMobileScreen ? '230px' : '450px' }"
            id="video-preview"
            controls
            :src="course.previewVideoUrl"
          />
        </div>
      </div>
      <page-separator title="Passing Criteria" />
      <b-form-group label-for="passing_percentage">
        <label for="passing_percentage" class="form-label"
          >Passing Percentage
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="
              `The passing percentage is determined based on quiz performance and the successful completion of assigned tasks.`
            "
            >info</i
          ></label
        >
        <b-form-input
          id="passing_percentage"
          placeholder="Enter Passing Percentage"
          v-model="course.passingPercentage"
          type="number"
          min="0"
          max="100"
          required
        />
      </b-form-group>
      <b-form-group label="Final Exam" label-for="exam" label-class="form-label">
        <v-select
          id="exam"
          class="form-control v-select-custom"
          label="title"
          v-model="course.exam"
          :reduce="item => item.id"
          placeholder="Select Final Exam"
          :options="allExams"
        >
        </v-select>
      </b-form-group>
      <b-form-group label="Mock Exams" label-for="mock_exams" label-class="form-label">
        <v-select
          id="mock_exams"
          class="form-control v-select-custom"
          label="title"
          v-model="course.mockExams"
          :reduce="item => item.id"
          placeholder="Select Mock Exams"
          :options="allMockExams"
          multiple
        >
        </v-select>
      </b-form-group>
      <b-btn
        variant="primary"
        :disabled="isFormLoading || isUploadingVideoUrl"
        style="width: 150px"
        type="submit"
        class="btn-normal"
      >
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
//import { get, map } from 'lodash';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import Editor from '@tinymce/tinymce-vue';
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import Page from '../../../components/Page.vue';
// import UserAvatar from '../../../components/User/UserAvatar.vue';
import ImageUploader from '@/components/ImageUploader.vue';
import { FmvAvatar } from 'fmv-avatar';
import 'vue-swatches/dist/vue-swatches.css';
import { required } from 'vuelidate/lib/validators';
export default {
  components: {
    PageHeader,
    PageSeparator,
    Editor,
    ImageUploader,
    FmvAvatar,
  },
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Course' : 'Add Course',
      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      // Bumped after hydrating an existing course so TinyMCE remounts with the
      // loaded content; without this the policy editor renders empty on edit.
      editorKey: 0,
      course: {
        title: '',
        image: '',
        description: '',
        shortIntro: '',
        progCategory: '',
        assignedSchools: [],
        courseFee: '',
        previewVideoUrl: '',
        isPublished: true,
        passingPercentage: '',
        exam: '',
        mockExams: [],
        restrictNextLesson: false,
        restrictionOnStartDate: false,
        enableSkillLibrary: true,
        customTags: [],
        // State compliance (e.g. CA CNA). All default off/neutral.
        enforceReadTime: false,
        requirePassToAdvance: false,
        requireIdentityAttestation: false,
        minutesPerCreditHour: 50,
        requiredTheoryHours: null,
        coursePolicy: '',
      },
      canUpdateCategory: true,
      uploadFile: { image: null, video: null },
      uploadPercent: { image: 0, video: 0 },
      uploadCancelTokenSource: { image: null, video: null },
      isLoading: false,
      isImgUploading: false,
      areCareersLoading: false,
      allPrograms: [],
      allSchools: [],
      areSchoolsLoading: false,
      isUploadingVideoUrl: false,
      windowWidth: window.innerWidth,
      allExams: [],
      allMockExams: [],
    };
  },

  watch: {
    // Mastery presupposes sequencing: holding a student back for failing an
    // assessment only means anything if content unlocks in order. Turning this
    // on forces Restrict Next Lesson on (and the checkbox above disables so it
    // cannot be unticked underneath). Turning it off leaves sequencing where it
    // is -- it is a valid setting on its own.
    'course.requirePassToAdvance'(enabled) {
      if (enabled) {
        this.course.restrictNextLesson = true;
      }
    },
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Courses', to: { name: 'list-courses' } },
        {
          text: this.$route.params.id ? 'Edit Course' : 'Add Course',
          active: true,
        },
      ];
    },
    isFormLoading() {
      return this.isLoading || this.isLogoUploading || this.isProfileBgImgUploading;
    },
    isMobileScreen() {
      return this.windowWidth <= 420;
    },
  },
  validations() {
    return {
      course: {
        description: { required },
        progCategory: { required },
      },
    };
  },
  methods: {
    ...mapActions('courses', [
      'getCourse',
      'createCourse',
      'updateCourse',
      'getCourseImageUploadPresignedUrl',
      'canUpdateCourseCategory',
    ]),
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('school', ['getAllSchools']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('quizzes', ['getQuizzes']),

    setImage(file, fileType) {
      this.uploadFile[fileType] = file;
      this.uploadImage(fileType);
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadVideo(file);
      }
    },
    async uploadVideo(file) {
      this.isUploadingVideoUrl = true;

      if (file.size > 25000000) {
        this.makeToast({ variant: 'danger', msg: 'File size should not exceed 25MB' });
        this.isUploadingVideoUrl = false;
        return;
      }
      if (!['video/mp4', 'video/webm'].includes(file.type)) {
        this.makeToast({ variant: 'danger', msg: 'File type should be mp4 or webm' });

        this.isUploadingVideoUrl = false;
        return;
      }

      try {
        const urlResp = await this.getCourseImageUploadPresignedUrl({
          file_name: file.name,
          content_type: file.type,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.data.upload_url,
          file: file,
        });
        this.course.previewVideoUrl = urlResp.data.upload_url.split('?')[0];
      } catch (error) {
        this.isUploadingVideoUrl = false;
        return;
      }
      this.isUploadingVideoUrl = false;
    },
    async uploadImage(uploadType) {
      this.uploadCancelTokenSource[uploadType] = axios.CancelToken.source();
      this.isImgUploading = uploadType === 'image';

      try {
        const urlResp = await this.getCourseImageUploadPresignedUrl({
          file_name: this.uploadFile[uploadType].name,
          content_type: this.uploadFile[uploadType].type,
          upload_type: 'images',
        });
        await this.uploadToPresignedUrl({
          url: urlResp.data.upload_url,
          file: this.uploadFile[uploadType],
          config: {
            onUploadProgress: function(progressEvent) {
              this.uploadPercent[uploadType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource[uploadType].token,
          },
        });
        if (uploadType === 'image') {
          this.course.image = urlResp.data.upload_url.split('?')[0];
        }
      } catch (error) {
        this.uploadFile[uploadType] = null;
      }

      this.uploadCancelTokenSource[uploadType] = null;
      this.uploadPercent[uploadType] = 0;

      if (uploadType === 'image') this.isImgUploading = false;
    },

    async onSubmit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.isLoading = true;
        try {
          const data = {
            title: this.course.title,
            short_intro: this.course.shortIntro,
            description: this.course.description,
            image_url: this.course.image,
            program_category: this.course.progCategory,
            assigned_school: this.course.assignedSchools,
            course_fee: this.course.courseFee === '' || this.course.courseFee === null ? 0 : this.course.courseFee,
            preview_video_url: this.course.previewVideoUrl,
            is_published: this.course.isPublished,
            school: null,
            passing_percentage: this.course.passingPercentage,
            exam: this.course.exam,
            mock_exams: this.course.mockExams,
            restrict_next_lesson: this.course.restrictNextLesson,
            restriction_on_start_date: this.course.restrictionOnStartDate,
            enable_skill_library: this.course.enableSkillLibrary,
            custom_tags: this.course.customTags,
            // State compliance. Empty numeric inputs must go as null/default,
            // not '', or the API rejects them as invalid integers.
            enforce_read_time: this.course.enforceReadTime,
            require_pass_to_advance: this.course.requirePassToAdvance,
            require_identity_attestation: this.course.requireIdentityAttestation,
            minutes_per_credit_hour:
              this.course.minutesPerCreditHour === '' || this.course.minutesPerCreditHour === null
                ? 50
                : this.course.minutesPerCreditHour,
            required_theory_hours:
              this.course.requiredTheoryHours === '' || this.course.requiredTheoryHours === null
                ? null
                : this.course.requiredTheoryHours,
            course_policy: this.course.coursePolicy || null,
          };
          if (this.$route.params.id) {
            await this.updateCourse({
              id: this.$route.params.id,
              data,
            });
            this.makeToast({ variant: 'success', msg: this.$t('Course Updated!') });
          } else {
            await this.createCourse({
              ...data,
            });
            this.makeToast({ variant: 'success', msg: 'Course Added!' });
          }
          setTimeout(() => this.$router.push({ name: 'sa-list-courses' }), 250);
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }

        this.isLoading = false;
      }
    },
    removeVideo() {
      this.course.previewVideoUrl = '';
    },
    async fetchQuizzes() {
      const resp = await this.getQuizzes({ is_exam: true });
      this.allExams = resp.data || [];
    },
    async fetchMockExams() {
      const resp = await this.getQuizzes({ is_mock_exam: true });
      this.allMockExams = resp.data || [];
    },
    async fetchSchools() {
      this.areSchoolsLoading = true;
      try {
        const res = await this.getAllSchools();
        // Response structure: { status_code: 200, data: [...], message: "success" }
        this.allSchools = res.data || [];
      } catch (e) {
        console.error('Error fetching schools:', e);
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areSchoolsLoading = false;
    },
  },

  async mounted() {
    this.isLoading = true;
    this.fetchPrograms();
    this.fetchQuizzes();
    this.fetchMockExams();
    this.fetchSchools();
    try {
      if (this.$route.params.id) {
        const course_id = this.$route.params.id;
        const response = (await this.canUpdateCourseCategory({ course_id: course_id })).data;
        this.canUpdateCategory = response.can_update;
        const resp = (await this.getCourse(course_id)).data;
        this.course.title = resp.title;
        this.course.description = resp.description;
        this.course.shortIntro = resp.short_intro;
        this.course.previewVideoUrl = resp.preview_video_url;
        this.course.image = resp.image_url;
        this.course.progCategory = resp.program_category.id;
        this.course.isPublished = resp.is_published;
        this.course.passingPercentage = resp.passing_percentage;
        this.course.exam = resp.exam ? resp.exam.id : '';
        this.course.mockExams = resp.mock_exams ? resp.mock_exams.map(e => typeof e === 'object' ? e.id : e) : [];
        this.course.restrictNextLesson = resp.restrict_next_lesson || false;
        this.course.restrictionOnStartDate = resp.restriction_on_start_date || false;
        this.course.assignedSchools = resp.assigned_school || [];
        this.course.courseFee = resp.course_fee || '';
        this.course.enableSkillLibrary = resp.enable_skill_library !== undefined ? resp.enable_skill_library : true;
        this.course.customTags = Array.isArray(resp.custom_tags) ? resp.custom_tags : [];
        // State compliance
        this.course.enforceReadTime = resp.enforce_read_time || false;
        this.course.requirePassToAdvance = resp.require_pass_to_advance || false;
        this.course.requireIdentityAttestation = resp.require_identity_attestation || false;
        this.course.minutesPerCreditHour =
          resp.minutes_per_credit_hour !== undefined && resp.minutes_per_credit_hour !== null
            ? resp.minutes_per_credit_hour
            : 50;
        this.course.requiredTheoryHours =
          resp.required_theory_hours !== undefined ? resp.required_theory_hours : null;
        this.course.coursePolicy = resp.course_policy || '';
        // Remount TinyMCE so the loaded policy HTML actually appears.
        this.editorKey += 1;
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      this.$router.push({ name: 'sa-list-courses' });
    }

    this.isLoading = false;
  },
};
</script>
